// Button.tsx
import React, { FunctionComponent, useState, useEffect, MouseEvent, TouchEvent as ReactTouchEvent } from "react";
import styled, { keyframes, css } from "styled-components";
import Icon from "../global/Icon";
import Colors, { blendWithWhite, blendWithBlack } from "../global/Colors";
import Fonts from "../global/Fonts";
import _ from 'lodash';
import { default as styledComponents } from 'styled-components';

export const Text1 = styled.div<{ $color: string; $size: string | undefined }>`
    font-size: ${({ $size }) => ($size === 'large' ? '28px' : $size === 'medium' ? '22px' : $size === 'small' ? '18px' : $size === 'tiny' ? '14px' : $size === 'micro' ? '14px' : '18px')};
    line-height: 1.4em;
    font-weight: ${Fonts.lexendMedium.fontWeight};
    font-family: ${Fonts.lexendMedium.fontFamily};
    color: ${({ $color }) => $color};
    text-align: left;
    user-select: none;
`;

const ButtonPrimaryRoot = styledComponents.button.withConfig({
  shouldForwardProp: (prop) =>
      !['$backgroundColor', '$borderColor', '$iconTextColor', '$noText', '$fixed', '$rainbowShimmer', '$fullWidth'].includes(prop)
})<{
  $size?: 'tiny' | 'small' | 'medium' | 'large' | 'micro',
  disabled?: boolean,
  $noText?: boolean,
  $backgroundColor: string,
  $borderColor: string,
  $iconTextColor: string,
  $fixed?: boolean,
  $rainbowShimmer?: boolean;
  $fullWidth?: boolean;
}>`
  box-sizing: border-box;
  border: ${({ $borderColor }) => `1px solid ${$borderColor}`};
  color: ${({ $iconTextColor }) => $iconTextColor};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border-radius: ${({ $noText }) => $noText ? '50%' : '42px'};
  min-width: ${({ $noText, $size, $fixed }) => ($fixed ? '300px'
    : $noText ? ($size === 'large' ? '72px' : $size === 'medium' ? '52px' : $size === 'small' ? '42px' : $size === 'tiny' ? '36px' : $size === 'micro' ? '30px' : '42px')
    : 'fit-content')};
  min-height: ${({ $noText, $size, $fixed }) => ($fixed ? '52px'
    : $noText ? ($size === 'large' ? '72px' : $size === 'medium' ? '52px' : $size === 'small' ? '42px' : $size === 'tiny' ? '36px' : $size === 'micro' ? '30px' : '42px')
    : 'fit-content')};
  width: ${({ $fullWidth }) => $fullWidth ? '100%' : 'auto'};
  height: auto;
  padding: ${({ $noText, $size }) => {
      if ($noText && $size === 'micro') {
          return '5px';
      } else if ($noText) {
          return '10px';
      } else if ($size === 'large') {
          return '10px 22px 10px 24px';
      } else if ($size === 'medium') {
          return '8px 16px 8px 18px';
      } else if ($size === 'small') {
          return '8px 13px 8px 13px';
      } else if ($size === 'tiny') {
          return '8px 13px 8px 13px';
      } else if ($size === 'micro') {
          return '4.6px 13px 4.6px 13px';
      } else {
         return '8px 13px 8px 13px';
      }
  }};
  overflow: hidden; // Important for containing the shimmer pseudo-element
  position: relative; // Necessary for positioning the pseudo-element
  display: ${({ $fullWidth }) => $fullWidth ? 'flex' : 'inline-flex'};
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;
  white-space: nowrap;
  gap: ${({ $size }) =>
      $size === 'large' ? '10px' :
      $size === 'medium' ? '8px' :
      $size === 'small' ? '6px' :
      $size === 'tiny' ? '5px' :
      $size === 'micro' ? '5px' :
      '6px'};
  cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
  flex-shrink: 0;
  transition: transform 0.2s ease-in-out;
  transform: scale(1);

  &:hover {
    transform: ${({ disabled }) => disabled ? 'scale(1)' : 'scale(1.08)'};
  }

  &:active {
    transform: ${({ disabled }) => disabled ? 'scale(1)' : 'scale(1)'};
  }

  // Ensure content is above pseudo-elements
  > * {
    position: relative;
    z-index: 3;
  }

  // Rainbow border effect using ::after and ::before pseudo-elements
  ${({ $rainbowShimmer, disabled, $backgroundColor }) =>
    $rainbowShimmer && !disabled && css`
      // Rainbow gradient layer (slightly larger)
      &::after {
        content: '';
        position: absolute;
        top: -2px; // Adjust to cover border area
        left: -2px; // Adjust to cover border area
        right: -2px; // Adjust to cover border area
        bottom: -2px; // Adjust to cover border area
        z-index: 1; // Below the solid background
        border-radius: inherit; // Match the button's border-radius
        background: linear-gradient(
          110deg,
          red, orange, yellow, lime, green, cyan, blue, violet, purple, hotpink, red  
        );
        background-size: 400% 100%; // Make gradient wider than element for movement
        animation: ${rainbowShimmerAnimation} 20s linear infinite;
        opacity: 0.6;
        pointer-events: none;
      }

      // Solid background layer (slightly smaller, covers the center of ::after)
      &::before {
        content: '';
        position: absolute;
        top: 1px;
        left: 1px;
        right: 1px;
        bottom: 1px;
        z-index: 2; // Above ::after, below content
        border-radius: inherit;
        background-color: ${$backgroundColor};
        pointer-events: none;
      }
    `}
`;

const rainbowShimmerAnimation = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

export type ButtonType = {
    text?: string | null;
    icon?: string | null;
    onClick: (event?: MouseEvent<HTMLButtonElement> | ReactTouchEvent<HTMLButtonElement> | undefined) => void;
    onHover?: (isHovered: boolean) => void;
    disabled?: boolean;
    color?: string | null; // 'white' | 'black' | 'red' | 'ghost' | 'ghost pale' | 'ghost white' | 'blue' | 'pale blue' ;
    size?: 'tiny' | 'small' | 'medium' | 'large' | 'micro';
    fixed?: boolean;
    iconPosition?: 'left' | 'right';
    rainbowShimmer?: boolean;
    fullWidth?: boolean;
    children?: React.ReactNode;
};

export const setBackgroundColor = (color: string | null, state: string) => {
  let backgroundColor = Colors.blue;
  switch (color) {
    case 'blue':
      backgroundColor = state === 'hover' ? Colors.blueLight
        : state === 'active' ? Colors.blueDark
        : state === 'disabled' ? blendWithWhite(Colors.blue, 0.4)
        : Colors.blue;
      break;
    case 'white':
      backgroundColor = state === 'hover' ? Colors.white
        : state === 'active' ? Colors.grayVeryPale
        : state === 'disabled' ? Colors.white+40
        : Colors.white;
      break;
    case 'black':
      backgroundColor = state === 'hover' ? Colors.grey8
        : state === 'active' ? Colors.black
        : state === 'disabled' ? Colors.grey9+40
        : Colors.grey9;
      break;
    case 'red':
      backgroundColor = state === 'hover' ? Colors.redLight
        : state === 'active' ? Colors.redDark
        : state === 'disabled' ? blendWithWhite(Colors.red, 0.4)
        : Colors.red;
      break;
    case 'ghost':
    case 'ghost pale':
      backgroundColor = state === 'hover' ? Colors.white
        : state === 'active' ? Colors.grayPale
        : state === 'disabled' ? Colors.transparent
        : Colors.transparent;
      break;
    case 'ghost white':
      backgroundColor = state === 'hover' ? Colors.white+20
        : state === 'active' ? Colors.black+80
        : state === 'disabled' ? Colors.transparent
        : Colors.transparent;
      break;
    case 'pale blue':
      backgroundColor = state === 'hover' ? blendWithWhite(Colors.pale, 0.2)
        : state === 'active' ? Colors.paleBright
        : state === 'disabled' ? blendWithWhite(Colors.pale, 0.4)
        : Colors.pale;
      break;
    default:
      backgroundColor = Colors.blue;
  }
  return backgroundColor;
};

export const setBorderColor = (color: string | null, state: string) => {
  let borderColor = Colors.blue;
  switch (color) {
    case 'blue':
      borderColor = state === 'hover' ? Colors.blueLight
        : state === 'active' ? Colors.blueDark
        : state === 'disabled' ? blendWithWhite(Colors.blue, 0.4)
        : Colors.blue;
      break;
    case 'white':
      borderColor = state === 'hover' ? Colors.grayPale
        : state === 'active' ? Colors.grey2
        : state === 'disabled' ? Colors.transparent
        : Colors.grey1;
      break;
    case 'black':
      borderColor = state === 'hover' ? Colors.grey8
        : state === 'active' ? Colors.black
        : state === 'disabled' ? Colors.transparent
        : Colors.grey9;
      break;
    case 'red':
      borderColor = state === 'hover' ? Colors.redLight
        : state === 'active' ? Colors.redDark
        : state === 'disabled' ? blendWithWhite(Colors.red, 0.4)
        : Colors.red;
      break;
    case 'ghost':
    case 'ghost pale':
    case 'ghost white':
      borderColor = Colors.transparent
      // borderColor = state === 'hover' ? Colors.white+20
      //   : state === 'active' ? Colors.whiteTransparent
      //   : Colors.transparent;
      break;
    case 'pale blue':
      borderColor = state === 'hover' ? Colors.paleBright
        : state === 'active' ? Colors.blueDark
        : Colors.pale;
      break;
    default:
      borderColor = Colors.white;
  }
  return borderColor;
};

export const setIconTextColor = (color: string | null, state: string) => {
  let iconTextColor = Colors.blue;
  switch (color) {
    case 'blue':
      iconTextColor = Colors.white;
      break;
    case 'white':
      iconTextColor = state === 'hover' ? Colors.grey8
        : state === 'active' ? Colors.black
        : state === 'disabled' ? blendWithWhite(Colors.black, 0.05)
        : Colors.black;
      break;
    case 'black':
      iconTextColor = state === 'hover' ? Colors.white
        : state === 'active' ? Colors.white
        : state === 'disabled' ? blendWithBlack(Colors.white, 0.05)
        : Colors.white;
      break;
    case 'red':
      iconTextColor = Colors.white;
      break;
    case 'ghost':
      iconTextColor = state === 'disabled' ? blendWithWhite(Colors.grayMed, 0.4)
        : Colors.grayMed;
      break;
    case 'ghost pale':
      iconTextColor = state === 'disabled' ? blendWithBlack(Colors.grey4, 0.4)
        : Colors.grey4;
      break;
    case 'ghost white':
      iconTextColor = state === 'disabled' ? blendWithBlack(Colors.white, 0.4)
        : Colors.white;
      break;
    case 'pale blue':
      iconTextColor = state === 'hover' ? Colors.blueLight
        : state === 'active' ? Colors.blueDark
        : state === 'disabled' ? blendWithWhite(Colors.blue, 0.4)
        : Colors.blue;
      break;
    default:
      iconTextColor = Colors.white;
  }
  return iconTextColor;
};

export const getIconSize = (size: string | undefined) => {
    return size === 'large' ? 40 : size === 'medium' ? 24 : size === 'small' ? 22 : size === 'tiny' ? 16 : size === 'micro' ? 14 : 22;
};

const Button: FunctionComponent<ButtonType> = ({
    text = null,
    icon,
    onClick,
    onHover,
    disabled = false,
    color = null,
    size = 'small',
    fixed = false,
    iconPosition = 'right',
    rainbowShimmer = false,
    fullWidth = false,
    children,
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isActive, setIsActive] = useState(false);
  
    useEffect(() => {
      if (disabled) {
        setIsHovered(false);
        setIsActive(false);
      }
    }, [disabled]);
  
    const handleClick = _.debounce(() => {
      onClick();
    }, 500);
  
    const onClickRaw = (e: MouseEvent<HTMLButtonElement> | ReactTouchEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (!disabled) {
        // Type guard to ensure correct event type
        if (e.type === 'click' || e.type === 'mousedown' || e.type === 'mouseup') {
          onClick(e as MouseEvent<HTMLButtonElement>);
        } else if (e.type === 'touchstart' || e.type === 'touchend') {
          onClick(e as ReactTouchEvent<HTMLButtonElement>);
        }
      }
    };    
  
    const handleMouseEnter = () => {
      if (!disabled) {
        setIsHovered(true);
        if (onHover) {
          onHover(true);
        }
      }
    };
    
    const handleMouseLeave = () => {
      setIsHovered(false);
      setIsActive(false);
      if (onHover) {
        onHover(false);
      }
    };
  
    const noText = text === null;
  
    const state = disabled ? 'disabled' : isActive ? 'active' : isHovered ? 'hover' : 'default';
    const backgroundColor = setBackgroundColor(color, state);
    const borderColor = setBorderColor(color, state);
    const iconTextColor = setIconTextColor(color, state);
    const iconSize = getIconSize(size);
  
    return (
      <ButtonPrimaryRoot
        onClick={onClickRaw}
        disabled={disabled}
        $backgroundColor={backgroundColor}
        $borderColor={borderColor}
        $iconTextColor={iconTextColor}
        $noText={noText}
        $size={size}
        $fixed={fixed}
        $rainbowShimmer={rainbowShimmer}
        $fullWidth={fullWidth}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={() => !disabled && setIsActive(true)}
        onMouseUp={() => !disabled && setIsActive(false)}
        onTouchStart={() => !disabled && setIsActive(true)}
        onTouchEnd={() => !disabled && setIsActive(false)}
      >
        {children ? children : (
            iconPosition === 'left' ? (
                <>
                  {icon && <Icon onClick={()=>{}} icon={icon} color={iconTextColor} size={iconSize} />}
                  {text && <Text1 $color={iconTextColor} $size={size}>{text}</Text1>}
                </>
              ) : (
                <>
                  {text && <Text1 $color={iconTextColor} $size={size}>{text}</Text1>}
                  {icon && <Icon onClick={()=>{}} icon={icon} color={iconTextColor} size={iconSize} />}
                </>
              )
        )}
      </ButtonPrimaryRoot>
    );
};

export default Button;